"use client"

import { useState, useMemo } from "react"
import { Plus, Trash2, RotateCcw, AlertTriangle, Package } from "lucide-react"
import type { Idioma } from "@/lib/landing/idioma"
import { numeroEn } from "./formato"

interface Producto {
  id: number
  nombre: string
  consumoDiario: number
  unidad: string
  plazoEntrega: number
  margenSeguridad: number
}

const T = {
  es: {
    productos: [
      { id: 1, nombre: "Aceite oliva", consumoDiario: 2, unidad: "L", plazoEntrega: 3, margenSeguridad: 2 },
      { id: 2, nombre: "Carne picada", consumoDiario: 5, unidad: "kg", plazoEntrega: 1, margenSeguridad: 1 },
      { id: 3, nombre: "Tomate triturado", consumoDiario: 3, unidad: "kg", plazoEntrega: 5, margenSeguridad: 2 },
      { id: 4, nombre: "Pan rallado", consumoDiario: 0.5, unidad: "kg", plazoEntrega: 7, margenSeguridad: 3 },
    ] as Producto[],
    titulo: "Productos",
    limpiar: "Limpiar",
    phNombre: "Nombre del producto",
    consumoDia: "Consumo/día",
    unidad: "Unidad",
    uds: "uds",
    plazo: "Plazo (días)",
    margen: "Margen (días)",
    anadir: "Añadir producto",
    formulas: "📐 Fórmulas",
    formulaSeguridad: ["Stock seguridad", " = Consumo × Margen días"],
    formulaPedido: ["Punto pedido", " = (Consumo × Plazo) + Stock seguridad"],
    niveles: "Niveles de stock",
    sinNombre: "Sin nombre",
    porDia: (cantidad: string, unidad: string) => `${cantidad} ${unidad}/día`,
    stockMinimo: "Stock mínimo",
    puntoPedidoLargo: "Punto de pedido",
    puntoPedido: "Punto pedido",
    stockOptimo: "Stock óptimo",
    plazoLargo: "Plazo de entrega largo: considera aumentar el stock de seguridad",
    consejosTitulo: "💡 Gestión de stock",
    consejos: [
      ["Stock mínimo:", "Nivel de alerta, pide ya"],
      ["Punto pedido:", "Cuando llegues aquí, haz el pedido"],
      ["Stock óptimo:", "Nivel ideal para operar sin excesos"],
      ["", "Revisa consumos semanalmente para ajustar"],
    ],
  },
  en: {
    productos: [
      { id: 1, nombre: "Olive oil", consumoDiario: 2, unidad: "L", plazoEntrega: 3, margenSeguridad: 2 },
      { id: 2, nombre: "Minced beef", consumoDiario: 5, unidad: "kg", plazoEntrega: 1, margenSeguridad: 1 },
      { id: 3, nombre: "Crushed tomatoes", consumoDiario: 3, unidad: "kg", plazoEntrega: 5, margenSeguridad: 2 },
      { id: 4, nombre: "Breadcrumbs", consumoDiario: 0.5, unidad: "kg", plazoEntrega: 7, margenSeguridad: 3 },
    ] as Producto[],
    titulo: "Products",
    limpiar: "Reset",
    phNombre: "Product name",
    consumoDia: "Usage/day",
    unidad: "Unit",
    uds: "units",
    plazo: "Lead time (days)",
    margen: "Buffer (days)",
    anadir: "Add product",
    formulas: "📐 Formulas",
    formulaSeguridad: ["Safety stock", " = Usage × Buffer days"],
    formulaPedido: ["Reorder point", " = (Usage × Lead time) + Safety stock"],
    niveles: "Stock levels",
    sinNombre: "Unnamed",
    porDia: (cantidad: string, unidad: string) => `${cantidad} ${unidad}/day`,
    stockMinimo: "Minimum stock",
    puntoPedidoLargo: "Reorder point",
    puntoPedido: "Reorder point",
    stockOptimo: "Optimal stock",
    plazoLargo: "Long lead time: consider increasing your safety stock",
    consejosTitulo: "💡 Managing stock",
    consejos: [
      ["Minimum stock:", "Your alert level: order now"],
      ["Reorder point:", "Once you get here, place the order"],
      ["Optimal stock:", "The ideal level to operate without overstocking"],
      ["", "Review usage every week and adjust"],
    ],
  },
}

let nextId = 100

export default function StockMinimoCalculator({ idioma = "es" }: { idioma?: Idioma }) {
  const t = T[idioma]
  const defaultProductos = t.productos
  // null: sin decimales fijos (como se escribió)
  const fmt = (n: number, d: number | null) =>
    idioma === "en" ? (d === null ? n.toLocaleString("en-GB") : numeroEn(n, d)) : d === null ? `${n}` : n.toFixed(d)
  const unidad = (u: string) => (u === "uds" ? t.uds : u)
  const [productos, setProductos] = useState<Producto[]>(defaultProductos)

  const results = useMemo(() => {
    return productos.map((p) => {
      // Stock de seguridad = consumo diario × días de margen
      const stockSeguridad = p.consumoDiario * p.margenSeguridad

      // Punto de pedido = (consumo diario × plazo entrega) + stock seguridad
      const puntoPedido = p.consumoDiario * p.plazoEntrega + stockSeguridad

      // Stock mínimo = stock seguridad (el nivel más bajo aceptable)
      const stockMinimo = stockSeguridad

      // Stock máximo recomendado (para no tener exceso)
      const stockMaximo = puntoPedido + p.consumoDiario * 7 // Una semana extra

      return {
        ...p,
        stockSeguridad,
        puntoPedido,
        stockMinimo,
        stockMaximo,
      }
    })
  }, [productos])

  const addProducto = () => {
    setProductos([
      ...productos,
      { id: nextId++, nombre: "", consumoDiario: 0, unidad: "kg", plazoEntrega: 2, margenSeguridad: 1 },
    ])
  }

  const removeProducto = (id: number) => {
    if (productos.length > 1) {
      setProductos(productos.filter((p) => p.id !== id))
    }
  }

  const updateProducto = (id: number, field: keyof Producto, value: string | number) => {
    setProductos(
      productos.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    )
  }

  const reset = () => {
    setProductos(defaultProductos)
  }

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
      <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-zinc-200">
        {/* Inputs */}
        <div className="p-6 md:p-8 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-zinc-900">{t.titulo}</h2>
            <button
              onClick={reset}
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              {t.limpiar}
            </button>
          </div>

          <div className="space-y-4">
            {productos.map((p) => (
              <div key={p.id} className="bg-zinc-50 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={p.nombre}
                    onChange={(e) => updateProducto(p.id, "nombre", e.target.value)}
                    placeholder={t.phNombre}
                    className="flex-1 px-3 py-2 rounded-xl border border-zinc-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-lime-500"
                  />
                  <button
                    onClick={() => removeProducto(p.id)}
                    className="p-2 text-zinc-400 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">{t.consumoDia}</label>
                    <input
                      type="number"
                      value={p.consumoDiario || ""}
                      onChange={(e) =>
                        updateProducto(p.id, "consumoDiario", parseFloat(e.target.value) || 0)
                      }
                      placeholder="2"
                      min="0"
                      step="0.1"
                      className="w-full px-2 py-1.5 rounded-lg border border-zinc-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-lime-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">{t.unidad}</label>
                    <select
                      value={p.unidad}
                      onChange={(e) => updateProducto(p.id, "unidad", e.target.value)}
                      className="w-full px-2 py-1.5 rounded-lg border border-zinc-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-lime-500"
                    >
                      <option value="kg">kg</option>
                      <option value="L">L</option>
                      <option value="uds">{t.uds}</option>
                      <option value="g">g</option>
                      <option value="ml">ml</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">{t.plazo}</label>
                    <input
                      type="number"
                      value={p.plazoEntrega || ""}
                      onChange={(e) =>
                        updateProducto(p.id, "plazoEntrega", parseInt(e.target.value) || 0)
                      }
                      placeholder="3"
                      min="1"
                      className="w-full px-2 py-1.5 rounded-lg border border-zinc-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-lime-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">{t.margen}</label>
                    <input
                      type="number"
                      value={p.margenSeguridad || ""}
                      onChange={(e) =>
                        updateProducto(p.id, "margenSeguridad", parseInt(e.target.value) || 0)
                      }
                      placeholder="2"
                      min="0"
                      className="w-full px-2 py-1.5 rounded-lg border border-zinc-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-lime-500"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={addProducto}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-lime-600 hover:text-lime-700"
          >
            <Plus className="w-4 h-4" />
            {t.anadir}
          </button>

          {/* Formula explanation */}
          <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-sky-800 mb-2">{t.formulas}</h3>
            <div className="text-sm text-sky-700 space-y-1">
              <p><strong>{t.formulaSeguridad[0]}</strong>{t.formulaSeguridad[1]}</p>
              <p><strong>{t.formulaPedido[0]}</strong>{t.formulaPedido[1]}</p>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="p-6 md:p-8 bg-zinc-50 space-y-6">
          <h2 className="text-lg font-semibold text-zinc-900">{t.niveles}</h2>

          {/* Results per product */}
          <div className="space-y-4">
            {results.map((r) => (
              <div key={r.id} className="bg-white rounded-xl border border-zinc-200 p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-zinc-400" />
                    <span className="font-medium text-zinc-900">{r.nombre || t.sinNombre}</span>
                  </div>
                  <span className="text-xs text-zinc-500">{t.porDia(fmt(r.consumoDiario, null), unidad(r.unidad))}</span>
                </div>

                {/* Visual bar */}
                <div className="relative h-8 bg-zinc-100 rounded-lg overflow-hidden mb-3">
                  <div
                    className="absolute left-0 top-0 bottom-0 bg-red-200"
                    style={{ width: `${Math.min((r.stockMinimo / r.stockMaximo) * 100, 100)}%` }}
                  />
                  <div
                    className="absolute left-0 top-0 bottom-0 bg-amber-200"
                    style={{ width: `${Math.min((r.puntoPedido / r.stockMaximo) * 100, 100)}%` }}
                  />
                  <div
                    className="absolute left-0 top-0 bottom-0 bg-emerald-200"
                    style={{ width: `${Math.min((r.stockSeguridad / r.stockMaximo) * 100, 100)}%` }}
                  />
                  
                  {/* Markers */}
                  <div
                    className="absolute top-0 bottom-0 w-0.5 bg-red-500"
                    style={{ left: `${Math.min((r.stockMinimo / r.stockMaximo) * 100, 100)}%` }}
                    title={t.stockMinimo}
                  />
                  <div
                    className="absolute top-0 bottom-0 w-0.5 bg-amber-500"
                    style={{ left: `${Math.min((r.puntoPedido / r.stockMaximo) * 100, 100)}%` }}
                    title={t.puntoPedidoLargo}
                  />
                </div>

                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="text-center p-2 bg-red-50 rounded-lg">
                    <p className="text-xs text-red-600 font-medium">{t.stockMinimo}</p>
                    <p className="text-lg font-bold text-red-700">{fmt(r.stockMinimo, 1)}</p>
                    <p className="text-xs text-red-500">{unidad(r.unidad)}</p>
                  </div>
                  <div className="text-center p-2 bg-amber-50 rounded-lg">
                    <p className="text-xs text-amber-600 font-medium">{t.puntoPedido}</p>
                    <p className="text-lg font-bold text-amber-700">{fmt(r.puntoPedido, 1)}</p>
                    <p className="text-xs text-amber-500">{unidad(r.unidad)}</p>
                  </div>
                  <div className="text-center p-2 bg-emerald-50 rounded-lg">
                    <p className="text-xs text-emerald-600 font-medium">{t.stockOptimo}</p>
                    <p className="text-lg font-bold text-emerald-700">{fmt(r.stockMaximo, 1)}</p>
                    <p className="text-xs text-emerald-500">{unidad(r.unidad)}</p>
                  </div>
                </div>

                {r.plazoEntrega > 5 && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
                    <AlertTriangle className="w-4 h-4" />
                    <span>{t.plazoLargo}</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Tips */}
          <div className="bg-lime-50 border border-lime-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-lime-800 mb-2">{t.consejosTitulo}</h3>
            <ul className="text-sm text-lime-700 space-y-1">
              {t.consejos.map(([fuerte, resto]) => (
                <li key={resto}>
                  • {fuerte && <strong>{fuerte}</strong>}
                  {fuerte ? " " : ""}
                  {resto}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
